/* global Alpine */

/**
 * Componente Alpine per la pagina risultati: form modifica, mappa, modale tariffe.
 * Si aspetta: window.resultsMapData, window.onResultsRouteCalculated
 * initialData da x-data: from_name, to_name, from_id, to_id, date, time, pax, routeInfo, tariffsData
 * Stringhe tradotte: data-results-strings su <main> (JSON con chiavi cancellationUnknown, nonRefundable, ...)
 */
export default function resultsSearch() {
  document.addEventListener('alpine:init', () => {
    Alpine.data('resultsSearch', (initialData) => {
      const routeInfo = initialData.routeInfo || {
        distanceKm: 0,
        durationText: '',
      }

      return {
        langPrefix: initialData.langPrefix || '/',
        isEditing: false,
        extrasOpen: false,
        showTariffModal: false,
        selectedTariff: null,
        cartLoading: false,
        cartExists: false,
        customerData: {
          contact_firstname: '',
          contact_lastname: '',
          contact_email: '',
          contact_phone_prefix: '39',
          contact_phone: '',
        },
        pendingTransferData: null,

        /** true se l'utente ha modificato i campi di ricerca senza aver cliccato "Aggiorna ricerca" */
        searchParamsDirty: false,

        /** Modale mostrato dopo l'aggiunta al carrello (Vai al carrello / Continua acquisti) */
        showAddToCartSuccessModal: false,

        /** Stringhe lette da data-results-strings (tradotte con _() in PHP) */
        strings: {},

        from: initialData.from_name || '',
        to: initialData.to_name || '',
        from_id: initialData.from_id || '',
        to_id: initialData.to_id || '',
        // Coordinate del punto d'incontro raffinate in home (vuote se non riposizionato)
        from_lat: initialData.from_lat || '',
        from_lng: initialData.from_lng || '',
        to_lat: initialData.to_lat || '',
        to_lng: initialData.to_lng || '',
        date: initialData.date || '',
        time: initialData.time || '',
        pax: String(initialData.pax || 1),
        bags: String(initialData.bags || 0),
        childseats: String(initialData.childseats || 0),
        pets: String(initialData.pets || 0),

        from_loc: null,
        to_loc: null,

        routeInfo: { ...routeInfo },

        /** Dati tariffe per il modale (da x-data) */
        tariffsData: initialData.tariffsData || {},

        /** Minuti di anticipo minimi rispetto al pickup (da PHP, default 30) */
        minMinutesBeforePickup: initialData.minMinutesBeforePickup ?? 30,

        suggestions: [],
        showSuggestions: false,
        activeField: null,

        /**
         * Coordinate del punto d'incontro da inviare al carrello.
         * Include solo i valori numerici validi: se assenti, il backend ripiega
         * sulle coordinate della località.
         */
        _coordFields() {
          const out = {}
          const add = (key, val) => {
            const n = parseFloat(val)
            if (!Number.isNaN(n)) out[key] = n
          }
          add('from_lat', this.from_lat)
          add('from_lng', this.from_lng)
          add('to_lat', this.to_lat)
          add('to_lng', this.to_lng)
          return out
        },

        formatRouteInfo() {
          if (!this.routeInfo) return ''
          const km = Number(this.routeInfo.distanceKm).toLocaleString('it-IT', {
            minimumFractionDigits: 1,
            maximumFractionDigits: 1,
          })
          return `${km} km • ${this.routeInfo.durationText}`
        },

        init() {
          const raw = this.$el.getAttribute('data-results-strings')
          if (raw) {
            try {
              this.strings = JSON.parse(raw)
            } catch {
              this.strings = {}
            }
          }
          if (window.resultsMapData) {
            this.from_loc = window.resultsMapData.from
            this.to_loc = window.resultsMapData.to
            // Allinea i marker alle coordinate raffinate in home, se presenti
            if (this.from_loc && this.from_lat && this.from_lng) {
              this.from_loc.lat = parseFloat(this.from_lat)
              this.from_loc.lng = parseFloat(this.from_lng)
            }
            if (this.to_loc && this.to_lat && this.to_lng) {
              this.to_loc.lat = parseFloat(this.to_lat)
              this.to_loc.lng = parseFloat(this.to_lng)
            }
          }
          const self = this
          window.onResultsRouteCalculated = (info) => {
            if (info && info.distanceKm > 0) {
              self.routeInfo = {
                distanceKm: info.distanceKm,
                durationText: info.durationText || '',
              }
            }
          }
        },

        async handleInput(field, value) {
          this.searchParamsDirty = true
          this[field] = value
          this.activeField = field
          if (
            field === 'from' &&
            this.from_loc &&
            this.from_loc.name !== value
          ) {
            this.from_id = ''
            this.from_loc = null
            this.from_lat = ''
            this.from_lng = ''
          }
          if (field === 'to' && this.to_loc && this.to_loc.name !== value) {
            this.to_id = ''
            this.to_loc = null
            this.to_lat = ''
            this.to_lng = ''
          }
          if (value.length < 2) {
            this.suggestions = []
            this.showSuggestions = false
            return
          }
          try {
            const response = await fetch(
              `/api?endpoint=locations&q=${encodeURIComponent(value)}`
            )
            this.suggestions = await response.json()
            this.showSuggestions = this.suggestions.length > 0
          } catch (err) {
            console.error('Error fetching locations:', err)
            this.suggestions = []
          }
        },

        selectSuggestion(place) {
          this.searchParamsDirty = true
          if (this.activeField === 'from') {
            this.from = place.name
            this.from_id = place.id
            this.from_loc = place
            // Località cambiata in results: il raffinamento home non vale più,
            // si riparte dalle coordinate di default della nuova località.
            this.from_lat = place.lat ?? ''
            this.from_lng = place.lng ?? ''
          } else if (this.activeField === 'to') {
            this.to = place.name
            this.to_id = place.id
            this.to_loc = place
            this.to_lat = place.lat ?? ''
            this.to_lng = place.lng ?? ''
          }
          this.showSuggestions = false
          this.suggestions = []
          this.activeField = null
          this.updateMap()
        },

        swap() {
          const tempLoc = this.from_loc
          this.from_loc = this.to_loc
          this.to_loc = tempLoc

          const tempName = this.from
          this.from = this.to
          this.to = tempName

          const tempId = this.from_id
          this.from_id = this.to_id
          this.to_id = tempId

          const tempLat = this.from_lat
          const tempLng = this.from_lng
          this.from_lat = this.to_lat
          this.from_lng = this.to_lng
          this.to_lat = tempLat
          this.to_lng = tempLng

          this.searchParamsDirty = true
          this.showSuggestions = false
          this.suggestions = []
          this.activeField = null

          this.updateMap()
        },

        updateMap() {
          if (!window.resultsMapInstance) return
          window.resultsMapInstance.setRoute(this.from_loc, this.to_loc)
        },

        isValid() {
          return this.from_id && this.to_id && this.date && this.time
        },

        minDateToday() {
          return new Date().toISOString().split('T')[0]
        },

        minTimeForPickup() {
          const today = new Date().toISOString().split('T')[0]
          if (!this.date || this.date !== today) return '00:00'
          const minMs = this.minMinutesBeforePickup * 60 * 1000
          const d = new Date(Date.now() + minMs)
          d.setMinutes(Math.ceil(d.getMinutes() / 30) * 30)
          d.setSeconds(0, 0)
          return d.toTimeString().slice(0, 5)
        },

        isDateTimeValid() {
          const today = new Date().toISOString().split('T')[0]
          if (this.date < today) return false
          if (this.date !== today) return true
          if (!this.time) return false
          const pickup = new Date(this.date + 'T' + this.time + ':00')
          const minPickup = new Date(
            Date.now() + this.minMinutesBeforePickup * 60 * 1000
          )
          return pickup.getTime() >= minPickup.getTime()
        },

        updateSearch() {
          if (!this.isValid()) return
          const today = new Date().toISOString().split('T')[0]
          if (this.date < today) {
            alert(
              this.strings.errorDatePast ||
                'La data non può essere nel passato.'
            )
            return
          }
          if (!this.isDateTimeValid()) {
            alert(
              this.strings.errorPickup30Min ||
                "Il transfer deve essere prenotato almeno 30 minuti prima dell'orario di pickup."
            )
            return
          }
          const params = new URLSearchParams({
            from_id: this.from_id,
            to_id: this.to_id,
            date: this.date,
            time: this.time,
            passengers: this.pax,
            luggage: String(this.bags || 0),
            childseats: String(this.childseats || 0),
            pets: String(this.pets || 0),
          })
          window.location.href = `${this.langPrefix}results?${params.toString()}`
        },

        async openTariffModal(tariffType) {
          if (this.searchParamsDirty) {
            alert(
              this.strings.searchParamsDirtyAlert ||
                'Aggiorna la ricerca per proseguire.'
            )
            return
          }
          if (this.tariffsData && this.tariffsData[tariffType]) {
            this.selectedTariff = this.tariffsData[tariffType]

            // Verifica se il carrello esiste
            try {
              const checkRes = await fetch('/api?endpoint=cart-check')
              const checkData = await checkRes.json().catch(() => ({}))
              this.cartExists = checkData.result && checkData.result.exists
            } catch {
              this.cartExists = false
            }

            // Se il carrello non esiste, prepara i dati del transfer per il form
            if (!this.cartExists) {
              const cardId = 'tariff-card-' + tariffType
              const cardElement = document.getElementById(cardId)
              if (cardElement) {
                const cartDataAttr = cardElement.getAttribute('data-cart-data')
                if (cartDataAttr) {
                  try {
                    const cardData = JSON.parse(cartDataAttr)
                    this.pendingTransferData = {
                      from_id: this.from_id,
                      to_id: this.to_id,
                      date: this.date,
                      time: this.time,
                      pax: String(this.pax),
                      bags: this.bags || 0,
                      childseats: this.childseats || 0,
                      pets: this.pets || 0,
                      ...this._coordFields(),
                      resource_type: cardData.resource_type || 'transfers',
                      resource_id: cardData.resource_id || '1',
                      price: cardData.price,
                      price_type: cardData.price_type || 'economy',
                    }
                  } catch {
                    this.pendingTransferData = null
                  }
                }
              }
            } else {
              this.pendingTransferData = null
            }

            this.showTariffModal = true
            document.body.style.overflow = 'hidden'
          }
        },

        closeTariffModal() {
          this.showTariffModal = false
          this.selectedTariff = null
          this.cartExists = false
          // Reset form dati cliente
          this.customerData = {
            contact_firstname: '',
            contact_lastname: '',
            contact_email: '',
            contact_phone_prefix: '39',
            contact_phone: '',
          }
          this.pendingTransferData = null
          document.body.style.overflow = ''
        },

        formatCancellationPolicy(policy) {
          const s = this.strings
          if (!policy)
            return {
              type: 'unknown',
              text: s.cancellationUnknown || 'Informazioni non disponibili',
            }

          if (!policy.refundable) {
            return {
              type: 'non-refundable',
              icon: 'circle-xmark',
              color: 'red',
              title: s.nonRefundable || 'Non rimborsabile',
              text:
                s.nonRefundableDesc ||
                'Questa prenotazione non è rimborsabile.',
            }
          }

          const deadline = new Date(policy.penalty_datetime)
          const deadlineStr = deadline.toLocaleDateString('it-IT', {
            weekday: 'long',
            day: 'numeric',
            month: 'long',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          })

          if (policy.penalty_datetime && deadline <= new Date()) {
            return {
              type: 'deadline-passed',
              icon: 'circle-info',
              color: 'yellow',
              text:
                s.nonRefundableWithin24h ||
                'Prenotazione non rimborsabile in quanto fatta meno di 24 ore dal servizio.',
            }
          }

          if (policy.penalty_amount === 0) {
            return {
              type: 'full-refund',
              icon: 'circle-check',
              color: 'green',
              title: s.cancelFree || 'Cancellazione gratuita',
              text: `${s.cancelFreeBy || 'Cancella gratuitamente entro'} ${deadlineStr}. ${s.cancelFreeAfter || 'Dopo questa data non sarà possibile ottenere alcun rimborso.'}`,
            }
          }

          return {
            type: 'partial-refund',
            icon: 'circle-info',
            color: 'yellow',
            title: s.cancelPenalty || 'Cancellazione con penale',
            text: `${s.cancelBy || 'Cancella entro'} ${deadlineStr} ${s.cancelPenaltyWith || 'con una penale di'} €${policy.penalty_amount.toFixed(2)}. ${s.cancelFreeAfter || 'Dopo questa data non sarà possibile ottenere alcun rimborso.'}`,
          }
        },

        async addToCart(cardId) {
          if (this.searchParamsDirty) {
            alert(
              this.strings.searchParamsDirtyAlert ||
                'Aggiorna la ricerca per proseguire.'
            )
            return
          }
          // Trova la card per ID
          const cardElement = document.getElementById(cardId)
          if (!cardElement) {
            alert('Card non trovata.')
            return
          }

          // Estrai il tipo di tariffa dall'ID della card (es. "tariff-card-standard" -> "standard")
          const tariffType = cardId.replace('tariff-card-', '')

          // Leggi i dati dall'attributo data-cart-data (contiene price, resource_type, resource_id)
          const cartDataAttr = cardElement.getAttribute('data-cart-data')
          if (!cartDataAttr) {
            alert('Dati carrello non disponibili sulla card.')
            return
          }

          let cardData
          try {
            cardData = JSON.parse(cartDataAttr)
          } catch {
            alert('Errore nel parsing dei dati della card.')
            return
          }

          // Costruisci il body combinando i dati aggiornati dal componente Alpine.js
          // con i dati specifici della tariffa dalla card
          const transferBody = {
            from_id: this.from_id,
            to_id: this.to_id,
            date: this.date,
            time: this.time,
            pax: String(this.pax),
            bags: this.bags || 0,
            childseats: this.childseats || 0,
            pets: this.pets || 0,
            ...this._coordFields(),
            resource_type: cardData.resource_type || 'transfers',
            resource_id: cardData.resource_id || '1',
            price: cardData.price,
            price_type: cardData.price_type || 'economy',
          }

          // Valida i campi obbligatori
          if (
            !transferBody.from_id ||
            !transferBody.to_id ||
            !transferBody.date ||
            !transferBody.time ||
            !transferBody.pax ||
            !transferBody.resource_type ||
            !transferBody.resource_id ||
            transferBody.price === undefined
          ) {
            alert('Dati carrello incompleti.')
            return
          }

          this.cartLoading = true
          try {
            // Prima chiamata: verifica se il carrello esiste
            const checkRes = await fetch('/api?endpoint=cart-check')
            const checkData = await checkRes.json().catch(() => ({}))
            if (!checkRes.ok || checkData.error) {
              alert(checkData.error || 'Errore nel controllo del carrello.')
              return
            }

            const cartExists = checkData.result && checkData.result.exists

            // Se il carrello non esiste, apri il modal della tariffa con il form
            if (!cartExists) {
              // Apri il modal della tariffa selezionata
              await this.openTariffModal(tariffType)
              // Salva i dati del transfer per usarli quando l'utente invia il form
              this.pendingTransferData = transferBody
              this.cartExists = false
              return
            }

            // Il carrello esiste, aggiungi direttamente il transfer
            await this.addTransferToCart(transferBody)
          } catch (err) {
            alert('Errore di connessione.' + err)
          } finally {
            this.cartLoading = false
          }
        },

        async addTransferToCart(transferBody, customerData = null) {
          // Combina i dati del transfer con eventuali dati cliente
          const body = { ...transferBody }
          if (customerData) {
            body.contact_firstname = customerData.contact_firstname
            body.contact_lastname = customerData.contact_lastname
            body.contact_email = customerData.contact_email
            body.contact_phone_prefix = customerData.contact_phone_prefix
            body.contact_phone = customerData.contact_phone
          }

          const res = await fetch('/api?endpoint=cart-add-transfer', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
          })
          const data = await res.json().catch(() => ({}))
          if (!res.ok || data.error) {
            alert(data.error || "Errore nell'aggiunta al carrello.")
            return false
          }

          this.closeTariffModal()
          this.showAddToCartSuccessModal = true
          document.body.style.overflow = 'hidden'
          return true
        },

        goToCart() {
          this.showAddToCartSuccessModal = false
          document.body.style.overflow = ''
          window.location.href = `${this.langPrefix}cart`
        },

        closeAddToCartSuccessModal() {
          this.showAddToCartSuccessModal = false
          document.body.style.overflow = ''
        },

        async submitCustomerDataFromModal() {
          // Verifica che ci siano dati del transfer pendenti
          if (!this.pendingTransferData || !this.selectedTariff) {
            alert('Dati transfer non disponibili.')
            return
          }

          // Valida i campi
          if (
            !this.customerData.contact_firstname ||
            !this.customerData.contact_lastname ||
            !this.customerData.contact_email ||
            !this.customerData.contact_phone_prefix ||
            !this.customerData.contact_phone
          ) {
            const formEl = document.getElementById('tariff-modal-customer-form')
            if (formEl) {
              formEl.scrollIntoView({ behavior: 'smooth', block: 'start' })
            }
            alert('Compila tutti i campi obbligatori.')
            return
          }

          // Valida email
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
          if (!emailRegex.test(this.customerData.contact_email)) {
            const formEl = document.getElementById('tariff-modal-customer-form')
            if (formEl) {
              formEl.scrollIntoView({ behavior: 'smooth', block: 'start' })
            }
            alert('Inserisci un indirizzo email valido.')
            return
          }

          this.cartLoading = true
          try {
            await this.addTransferToCart(
              this.pendingTransferData,
              this.customerData
            )
          } catch (err) {
            alert('Errore di connessione.' + err)
          } finally {
            this.cartLoading = false
          }
        },

        selectTariff(tariffType) {
          this.addToCart('tariff-card-' + tariffType)
          this.closeTariffModal()
        },
      }
    })
  })
}
